import { useRef, useState } from 'react';
import { Link } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import type { AppTheme } from '@/utils/theme';
import { Icon } from '@/components/ui/icon';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useStrings } from '@/i18n/use-strings';
import { StartOverDialog } from './start-over-dialog';

/**
 * Secondary navigation. Every destination is one tap from any screen, and the
 * one destructive action is pushed to the bottom behind a divider and a dialog
 * so it can never be the thing a thumb finds by accident (ticket 09 §8).
 *
 * The list is deliberately type-led rather than icon-led: the sprite has no
 * honest glyph for Privacy, Terms or Settings, and repeating a tick or a
 * thumbs-up next to them would say something untrue about each row.
 */
export function MenuSheet({
  open,
  onOpenChange,
  theme,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Theme segment for the `/h/:theme` destinations. */
  theme: AppTheme;
}) {
  const { header } = useStrings();

  const [startOverOpen, setStartOverOpen] = useState(false);
  const nav = useRef<HTMLElement>(null);

  const items = [
    { to: `/h/${theme}/learn/topics`, label: header.menu_journey },
    { to: `/h/${theme}/profile?mode=settings`, label: header.menu_settings },
    { to: '/about', label: header.menu_about },
    { to: '/resources', label: header.menu_resources },
    { to: '/contact', label: header.menu_contact },
    { to: '/privacy', label: header.menu_privacy },
    { to: '/terms', label: header.menu_terms },
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="end"
          closeLabel={header.header_menu_close}
          className="gap-0 bg-white"
          // Radix would otherwise land focus on `Start over`. The first
          // destination is the only sensible place to open a menu.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            nav.current?.querySelector<HTMLAnchorElement>('a')?.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>{header.header_menu_label}</SheetTitle>
          </SheetHeader>

          <nav ref={nav} aria-label={header.header_menu_label} className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <ul className="flex flex-col">
              {items.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => onOpenChange(false)}
                    className={cn(
                      'text-surf-dark-blue flex min-h-12 items-center gap-3 rounded-xl px-3 text-base font-semibold',
                      'transition-[background-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
                      'hover:bg-surf-blue/[0.06] focus-visible:ring-surf-blue/40 focus-visible:ring-4 focus-visible:outline-none',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    <Icon
                      name="passport/chevron-right"
                      aria-hidden
                      className="text-surf-dark-blue/30 size-4 shrink-0 rtl:rotate-180"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* The divider is structure, not decoration: it is what makes the
              action below it read as a different kind of thing. */}
          <div className="border-surf-gray border-t p-3">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                setStartOverOpen(true);
              }}
              className={cn(
                'text-destructive flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-base font-semibold',
                'transition-[background-color,scale] duration-150 active:scale-96 motion-reduce:transition-none',
                'hover:bg-destructive/5 focus-visible:ring-destructive/30 focus-visible:ring-4 focus-visible:outline-none',
              )}
            >
              <Icon name="passport/reset" aria-hidden className="size-5 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-start">{header.menu_start_over}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Outside the sheet so closing the sheet does not unmount the dialog. */}
      <StartOverDialog open={startOverOpen} onOpenChange={setStartOverOpen} />
    </>
  );
}
